import { logError } from 'util/logging';

interface ImageClipboardMatcherProps {
  insertAndUploadImage: (filename: string, file: Blob) => Promise<any>;
  quillRef: React.RefObject<any>;
}

export function createImageClipboardMatcher({ insertAndUploadImage, quillRef }: ImageClipboardMatcherProps) {
  return function imageClipboardMatcher(node: HTMLImageElement, delta: any) {
    const Delta = require('quill').import('delta');
    const src = node.src;

    // If it's already a Cloudinary URL, allow normal paste behavior
    if (src.includes('cloudinary.com')) {
      return delta;
    }

    // Helper function to insert uploaded image into editor
    const insertUploadedImage = (imageUrl: string) => {
      const editor = quillRef.current?.getEditor();
      if (!editor) return;

      const range = editor.getSelection();
      const index = range ? range.index : editor.getLength();
      editor.insertEmbed(index, 'image', imageUrl, 'user');
      editor.insertText(index + 1, '\n', 'silent');
      editor.setSelection(index + 1, 0);
    };

    // Helper function to handle image upload
    const handleImageUpload = (filename: string, blob: Blob) => {
      insertAndUploadImage(filename, blob)
        .then(response => {
          if (response?.secure_url) {
            insertUploadedImage(response.secure_url);
          }
        })
        .catch(error => {
          logError(error);
        });
    };

    // Handle base64 data URLs (screenshots, etc.)
    if (src.startsWith('data:')) {
      const [header, data] = src.split(',');
      const mimeType = header.match(/data:([^;]+)/)?.[1] || 'image/png';
      const extension = mimeType.split('/')[1] || 'png';
      const filename = `pasted-image-${Date.now()}.${extension}`;

      // Convert base64 to blob
      const byteCharacters = atob(data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      handleImageUpload(filename, blob);
      return new Delta(); // Prevent default paste
    }

    // Handle external URLs
    if (src.startsWith('http')) {
      fetch(src)
        .then(response => response.blob())
        .then(blob => {
          const extension = src.split('.').pop()?.split('?')[0] || 'png';
          const filename = `pasted-image-${Date.now()}.${extension}`;
          handleImageUpload(filename, blob);
        })
        .catch(error => {
          logError(error);
        });

      return new Delta(); // Prevent default paste
    }

    // For any other case, allow normal paste behavior
    return delta;
  };
}
